import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, del } from '@/lib/api'

import { useCurrentUser } from '../system/use-auth'

import type { CompanyRole, Permission } from '@company/shared'

/**
 * 权限范围类型
 */
type PermissionScope = boolean | string[]

/**
 * 用户权限
 */
export interface UserPermissions {
  role: string
  teamIds: string[]
  projectIds: string[]
  permissions: {
    finance: { read: boolean; write: boolean }
    hr: { read: boolean; write: boolean }
    projects: { read: PermissionScope; write: PermissionScope }
    teams: { read: PermissionScope; write: PermissionScope }

    workflow_templates: { create: string[]; read: boolean }
    workflow_analytics: { read: PermissionScope }
  }
}

/**
 * 数据范围
 */
export interface DataScope {
  type: 'all' | 'team' | 'project' | 'self' | 'none'
  teamIds?: string[]
  projectIds?: string[]
  employeeId?: string
}

/**
 * 权限检查结果
 */
export interface PermissionCheckResult {
  allowed: boolean
  reason?: string
  scope?: DataScope
}

/**
 * 资源权限
 */
export interface ResourcePermission {
  employeeId: string
  employeeName: string
  role: 'owner' | 'admin' | 'member' | 'viewer'
  grantedBy: string
}

// Query keys
export const permissionKeys = {
  all: ['permissions'] as const,
  my: () => [...permissionKeys.all, 'my'] as const,
  dataScope: (resourceType: string) => [...permissionKeys.all, 'dataScope', resourceType] as const,
  resource: (resourceType: string, resourceId: string) =>
    [...permissionKeys.all, 'resource', resourceType, resourceId] as const,
}

/**
 * 权限检查 Hook
 * 提供 hasPermission 和 hasRole 函数用于权限检查
 */
export function usePermissions() {
  const { data: user } = useCurrentUser()

  /**
   * 检查用户是否拥有指定权限
   * 支持通配符匹配，如 'project.*' 匹配 'project.create', 'project.edit' 等
   */
  const hasPermission = (permission: Permission): boolean => {
    if (!user) { return false }

    // director 拥有所有权限
    if (user.role === 'director') { return true }

    // 检查是否有通配符权限
    if (user.permissions.includes('*')) { return true }

    // 精确匹配
    if (user.permissions.includes(permission)) { return true }

    // 通配符匹配：检查 'resource.*' 是否匹配 'resource.action'
    const parts = permission.split('.')
    if (parts.length >= 2) {
      const wildcardPermission = `${parts[0]}.*`
      if (user.permissions.includes(wildcardPermission)) { return true }

      // 检查更细粒度的通配符，如 'resource.scope.*'
      if (parts.length >= 3) {
        const scopeWildcard = `${parts[0]}.${parts[1]}.*`
        if (user.permissions.includes(scopeWildcard)) { return true }
      }
    }

    return false
  }

  /**
   * 检查用户是否拥有指定角色之一
   */
  const hasRole = (...roles: CompanyRole[]): boolean => {
    if (!user) { return false }

    // director 拥有所有角色权限
    if (user.role === 'director') { return true }

    return roles.includes(user.role)
  }

  return {
    hasPermission,
    hasRole,
    user,
    permissions: user?.permissions ?? [],
    role: user?.role,
  }
}

/**
 * 获取当前用户权限
 */
export function useMyPermissions() {
  return useQuery({
    queryKey: permissionKeys.my(),
    queryFn: () => get<UserPermissions>('/users/me/permissions'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * 获取数据范围
 */
export function useDataScope(resourceType: string) {
  return useQuery({
    queryKey: permissionKeys.dataScope(resourceType),
    queryFn: () => get<DataScope>(`/users/me/data-scope/${resourceType}`),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * 检查权限
 */
export function useCheckPermission() {
  return useMutation({
    mutationFn: (params: {
      resourceType: string
      action: 'read' | 'write' | 'delete' | 'approve' | 'manage' | 'create'
      resourceId?: string
    }) => post<PermissionCheckResult>('/users/me/check-permission', params),
  })
}

/**
 * 批量检查权限
 */
export function useCheckPermissionsBatch() {
  return useMutation({
    mutationFn: (params: {
      resourceType: string
      action: 'read' | 'write' | 'delete' | 'approve' | 'manage' | 'create'
      resourceIds: string[]
    }) => post<Record<string, boolean>>('/users/me/check-permissions-batch', params),
  })
}

/**
 * 获取资源权限列表
 */
export function useResourcePermissions(
  resourceType: 'project' | 'team' | 'issue' | 'workflow_template',
  resourceId: string
) {
  return useQuery({
    queryKey: permissionKeys.resource(resourceType, resourceId),
    queryFn: () => get<ResourcePermission[]>(`/users/resources/${resourceType}/${resourceId}/permissions`),
    enabled: !!resourceId,
  })
}

/**
 * 授予资源权限
 */
export function useGrantPermission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      resourceType: 'project' | 'team' | 'issue' | 'workflow_template'
      resourceId: string
      employeeId: string
      role: 'owner' | 'admin' | 'member' | 'viewer'
    }) =>
      post<null>(`/users/resources/${params.resourceType}/${params.resourceId}/permissions`, {
        employeeId: params.employeeId,
        role: params.role,
      }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: permissionKeys.resource(variables.resourceType, variables.resourceId),
      })
    },
  })
}

/**
 * 撤销资源权限
 */
export function useRevokePermission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      resourceType: 'project' | 'team' | 'issue' | 'workflow_template'
      resourceId: string
      employeeId: string
    }) =>
      del<null>(
        `/users/resources/${params.resourceType}/${params.resourceId}/permissions/${params.employeeId}`
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: permissionKeys.resource(variables.resourceType, variables.resourceId),
      })
    },
  })
}

// ==================== 权限检查辅助函数 ====================

/**
 * 检查是否有财务访问权限
 */
export function hasFinanceAccess(permissions: UserPermissions | undefined): boolean {
  if (!permissions) { return false }
  return permissions.permissions.finance.read
}

/**
 * 检查是否有财务写入权限
 */
export function hasFinanceWriteAccess(permissions: UserPermissions | undefined): boolean {
  if (!permissions) { return false }
  return permissions.permissions.finance.write
}

/**
 * 检查是否有 HR 访问权限
 */
export function hasHrAccess(permissions: UserPermissions | undefined): boolean {
  if (!permissions) { return false }
  return permissions.permissions.hr.read
}

/**
 * 检查是否有 HR 写入权限
 */
export function hasHrWriteAccess(permissions: UserPermissions | undefined): boolean {
  if (!permissions) { return false }
  return permissions.permissions.hr.write
}

/**
 * 检查是否可以访问项目
 */
export function canAccessProject(
  permissions: UserPermissions | undefined,
  projectId: string
): boolean {
  if (!permissions) { return false }
  const projectRead = permissions.permissions.projects.read
  if (projectRead === true) { return true }
  if (Array.isArray(projectRead)) { return projectRead.includes(projectId) }
  return false
}

/**
 * 检查是否可以编辑项目
 */
export function canEditProject(
  permissions: UserPermissions | undefined,
  projectId: string
): boolean {
  if (!permissions) { return false }
  const projectWrite = permissions.permissions.projects.write
  if (projectWrite === true) { return true }
  if (Array.isArray(projectWrite)) { return projectWrite.includes(projectId) }
  return false
}

/**
 * 检查是否可以访问团队
 */
export function canAccessTeam(permissions: UserPermissions | undefined, teamId: string): boolean {
  if (!permissions) { return false }
  const teamRead = permissions.permissions.teams.read
  if (teamRead === true) { return true }
  if (Array.isArray(teamRead)) { return teamRead.includes(teamId) }
  return false
}

/**
 * 检查是否可以编辑团队
 */
export function canEditTeam(permissions: UserPermissions | undefined, teamId: string): boolean {
  if (!permissions) { return false }
  const teamWrite = permissions.permissions.teams.write
  if (teamWrite === true) { return true }
  if (Array.isArray(teamWrite)) { return teamWrite.includes(teamId) }
  return false
}

/**
 * 检查是否可以创建工作流模板
 */
export function canCreateWorkflowTemplate(
  permissions: UserPermissions | undefined,
  scope: 'system' | 'team' | 'project'
): boolean {
  if (!permissions) { return false }
  return permissions.permissions.workflow_templates.create.includes(scope)
}

/**
 * 检查是否可以查看工作流分析
 */
export function canViewWorkflowAnalytics(
  permissions: UserPermissions | undefined,
  projectId?: string
): boolean {
  if (!permissions) { return false }
  const analyticsRead = permissions.permissions.workflow_analytics.read
  if (analyticsRead === true) { return true }
  if (Array.isArray(analyticsRead) && projectId) { return analyticsRead.includes(projectId) }
  return false
}

/**
 * 获取用户可访问的项目 ID 列表
 */
export function getAccessibleProjectIds(permissions: UserPermissions | undefined): string[] | 'all' {
  if (!permissions) { return [] }
  const projectRead = permissions.permissions.projects.read
  if (projectRead === true) { return 'all' }
  if (Array.isArray(projectRead)) { return projectRead }
  return []
}

/**
 * 获取用户可访问的团队 ID 列表
 */
export function getAccessibleTeamIds(permissions: UserPermissions | undefined): string[] | 'all' {
  if (!permissions) { return [] }
  const teamRead = permissions.permissions.teams.read
  if (teamRead === true) { return 'all' }
  if (Array.isArray(teamRead)) { return teamRead }
  return []
}
